import { useState } from "react";
import type { AgentWorkbenchPatch } from "../../../../shared/types";
import {
  useApplyBrandVoiceWorkbenchPatch,
  useBrandVoice,
  useBrandVoiceWorkbench,
  useProposeBrandVoiceWorkbenchPatch,
  useUpdateBrandVoice,
} from "../../hooks/useBrand";
import { artifactWorkspaceViewModel } from "../../lib/artifact-workspace";
import { BrandVoice } from "../brand/BrandVoice";
import { VoicePatchHistory } from "./VoicePatchHistory";
import { ArtifactDocumentReview } from "../workbench/ArtifactDocumentReview";

type VoiceWorkspaceSurfaceProps = {
  brandId: string;
  brandName: string;
  workspaceHref?: string | null;
  locationLabel?: string;
};

export function VoiceWorkspaceSurface({
  brandId,
  brandName,
  locationLabel = "Brand docs / Voice",
}: VoiceWorkspaceSurfaceProps) {
  const { data: voiceData, isLoading, isError } = useBrandVoice(brandId);
  const {
    data: workbenchData,
    isLoading: isWorkbenchLoading,
    isError: isWorkbenchError,
  } = useBrandVoiceWorkbench(brandId);
  const updateVoice = useUpdateBrandVoice(brandId);
  const proposePatch = useProposeBrandVoiceWorkbenchPatch(brandId);
  const applyPatch = useApplyBrandVoiceWorkbenchPatch(brandId);
  const [proposal, setProposal] = useState<AgentWorkbenchPatch | null>(null);
  const [lastRequest, setLastRequest] = useState<{
    target_section_id?: string | null;
    quick_action_id?: string | null;
    instruction?: string | null;
  } | null>(null);
  const [proposalError, setProposalError] = useState<string | null>(null);
  const statusLabel = workbenchData?.data?.status
    ? workbenchData.data.status
        .split("_")
        .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
        .join(" ")
    : "Configured";
  const workspaceModel = workbenchData?.data
    ? artifactWorkspaceViewModel(workbenchData.data, {
      provenanceLabel: "voice-system.md",
      proposal,
    })
    : null;

  function acceptProposal(nextProposal: AgentWorkbenchPatch) {
    setProposal(nextProposal);
  }

  if (isWorkbenchLoading) {
    return (
      <div className="flex min-h-[120px] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (isWorkbenchError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        Could not load this brand voice workbench.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">{locationLabel}</span>
        <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">{brandName}</span>
        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-semibold text-emerald-700">
          {workspaceModel?.statusLabel ?? statusLabel}
        </span>
        {workspaceModel?.updatedAtLabel ? (
          <span className="rounded-full bg-slate-50 px-2.5 py-1 ring-1 ring-slate-200">
            Updated {workspaceModel.updatedAtLabel}
          </span>
        ) : null}
      </div>

      {workbenchData?.data
        ? (
          <ArtifactDocumentReview
            manifest={workbenchData.data}
            proposal={proposal}
            proposalError={proposalError}
            proposalPending={proposePatch.isPending}
            applyPending={applyPatch.isPending}
            provenanceLabel="voice-system.md"
            onCancelProposal={() => {
              setProposal(null);
              setProposalError(null);
            }}
            onRejectProposal={() => {
              setProposal(null);
              setProposalError(null);
            }}
            onAskAgainProposal={async () => {
              if (!lastRequest) return;
              try {
                setProposalError(null);
                const result = await proposePatch.mutateAsync(lastRequest);
                acceptProposal(result.data);
              } catch (error) {
                setProposal(null);
                setProposalError(error instanceof Error ? error.message : "Could not generate a new proposal.");
              }
            }}
            onApplyProposal={async () => {
              if (!proposal) return;
              try {
                setProposalError(null);
                await applyPatch.mutateAsync(proposal);
                setProposal(null);
              } catch (error) {
                setProposalError(error instanceof Error ? error.message : "Could not apply this proposal.");
              }
            }}
            onRequestProposal={async (request) => {
              setLastRequest(request);
              try {
                setProposalError(null);
                const result = await proposePatch.mutateAsync(request);
                acceptProposal(result.data);
                return true;
              } catch (error) {
                setProposal(null);
                setProposalError(error instanceof Error ? error.message : "Could not generate a proposal.");
                return false;
              }
            }}
          />
        )
        : (
          <div className="rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 shadow-sm">
            Voice artifact data is not available yet. Refresh this page or retry after the
            artifact loads.
          </div>
        )}

      <details className="rounded-[20px] border border-dashed border-slate-300 bg-slate-50/80 p-4 shadow-sm md:p-5">
        <summary className="flex cursor-pointer list-none flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-data text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
              Secondary tools
            </p>
            <p className="mt-2 max-w-2xl text-sm text-slate-600">
              Compatibility editing and learned history stay available, but they should not displace the canonical document.
            </p>
          </div>
          <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600">
            Expand
          </span>
        </summary>

        <div className="mt-4 space-y-3">
          <details className="group rounded-2xl border border-slate-200 bg-white shadow-sm">
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Compatibility editor</p>
                <p className="mt-1 text-sm text-slate-600">
                  Direct form editing remains available while markdown-first revisions become the
                  default workflow.
                </p>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                Voice pack draft
              </span>
            </summary>
            <div className="border-t border-slate-200 px-4 py-4">
              {isError ? (
                <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                  Could not load the legacy voice profile. The workbench remains available above while the compatibility editor recovers.
                </div>
              ) : isLoading ? (
                <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  Loading compatibility voice editor...
                </div>
              ) : null}
              <BrandVoice
                voice={voiceData?.data ?? null}
                onSave={(data) => updateVoice.mutate(data)}
                saving={updateVoice.isPending}
              />
            </div>
          </details>

          <details className="group rounded-2xl border border-slate-200 bg-white shadow-sm">
            <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-3 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">Voice history</p>
                <p className="mt-1 text-sm text-slate-600">
                  Review the learned corrections and current profile state without displacing the
                  canonical document.
                </p>
              </div>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                Learned corrections
              </span>
            </summary>
            <div className="border-t border-slate-200 px-4 py-4">
              <VoicePatchHistory brandId={brandId} brandName={brandName} />
            </div>
          </details>
        </div>
      </details>
    </div>
  );
}
